import './background.css';

import React, {Component} from 'react';
import GlyphField from './background/GlyphField';

/** Half of 60fps. The background is peripheral and this halves what it costs to draw. */
const kFrameInterval = 1 / 30;
/** Retina is worth having, beyond that it is just pixels nobody looks at. */
const kMaxPixelRatio = 2;
/** How far the pointer stirs the field into activity. */
const kPointerRadius = 156;

/** The backdrop behind the whole page, and the one filling the header bar on phones. */
export const kBackgroundPage = 'page';
export const kBackgroundHeader = 'header';

/**
 * The header band is only a few rows tall, so it wants a finer grid than the open page does, but not so
 * fine that the glyphs stop reading on a phone: this size drops one row from the bar and gives the rest
 * the space that row was taking.
 */
const kCellSizes = {
	[kBackgroundPage]: 26,
	[kBackgroundHeader]: 20
};

/**
 * Every surface draws from one animation frame rather than starting a loop of its own, so a phone
 * showing the header field and a desktop showing the page field cost the same single rAF chain.
 */
const backgroundSurfaces = new Set ();
let backgroundFrame = null;
let backgroundLastTimestamp = 0;
let backgroundSinceDraw = 0;

/**
 * One frame for every registered surface.
 */
function BackgroundFrame (timestamp) {
	backgroundFrame = window.requestAnimationFrame (BackgroundFrame);

	const deltaSeconds = backgroundLastTimestamp === 0 ? kFrameInterval : (timestamp - backgroundLastTimestamp) / 1000;
	backgroundLastTimestamp = timestamp;

	backgroundSinceDraw += deltaSeconds;

	if (backgroundSinceDraw < kFrameInterval) {
		return;
	}

	backgroundSurfaces.forEach (surface => surface.Draw (backgroundSinceDraw, true));
	backgroundSinceDraw = 0;
}

/**
 * Add a surface to the shared loop, starting the loop if it is the first.
 */
function RegisterBackgroundSurface (surface) {
	backgroundSurfaces.add (surface);

	if (backgroundFrame !== null) {
		return;
	}

	backgroundLastTimestamp = 0;
	backgroundSinceDraw = kFrameInterval;
	backgroundFrame = window.requestAnimationFrame (BackgroundFrame);
}

/**
 * Take a surface out of the shared loop, stopping the loop once the last one goes.
 */
function UnregisterBackgroundSurface (surface) {
	backgroundSurfaces.delete (surface);

	if (backgroundSurfaces.size > 0 || backgroundFrame === null) {
		return;
	}

	window.cancelAnimationFrame (backgroundFrame);
	backgroundFrame = null;
}

class Background extends Component {
	/**
	 * Background initialization.
	 */
	constructor (props) {
		super (props);

		this.canvas = React.createRef ();
		//Not this.context: React owns that property on Component and reassigns it on every render.
		this.canvasContext = null;
		this.renderer = null;
		this.running = false;
		this.colors = null;
		this.schemeObserver = null;
		this.pointer = null;

		this.reducedMotionQuery = window.matchMedia ('(prefers-reduced-motion: reduce)');
		this.mobileQuery = window.matchMedia ('(max-width: 767px)');

		this.OnResize = this.OnResize.bind (this);
		this.OnVisibilityChange = this.OnVisibilityChange.bind (this);
		this.OnPointerMove = this.OnPointerMove.bind (this);
		this.OnPointerLeave = this.OnPointerLeave.bind (this);
	}

	/**
	 * First rendered to DOM.
	 */
	componentDidMount () {
		const {variant} = this.props;

		this.renderer = new GlyphField (kCellSizes [variant]);
		this.canvasContext = this.canvas.current.getContext ('2d');

		this.ReadColors ();

		if (this.IsActive ()) {
			this.Size ();
			//Paint immediately rather than waiting on the first animation frame, so the surface is never blank.
			this.DrawOnce ();
		}

		window.addEventListener ('resize', this.OnResize);
		document.addEventListener ('visibilitychange', this.OnVisibilityChange);

		if (variant === kBackgroundPage) {
			//Passive: following the pointer must never hold up a scroll or a click.
			window.addEventListener ('pointermove', this.OnPointerMove, {passive: true});
			document.addEventListener ('pointerleave', this.OnPointerLeave);
		}

		//The scheme can change without React knowing, when the system flips while System is chosen.
		this.schemeObserver = new MutationObserver (() => {
			this.ReadColors ();
			this.DrawOnce ();
		});
		this.schemeObserver.observe (document.documentElement, {attributes: true, attributeFilter: ['data-color-scheme']});

		this.Start ();
	}

	/**
	 * Called before component is removed from DOM.
	 */
	componentWillUnmount () {
		this.Stop ();

		window.removeEventListener ('resize', this.OnResize);
		document.removeEventListener ('visibilitychange', this.OnVisibilityChange);
		window.removeEventListener ('pointermove', this.OnPointerMove);
		document.removeEventListener ('pointerleave', this.OnPointerLeave);

		if (this.schemeObserver !== null) {
			this.schemeObserver.disconnect ();
			this.schemeObserver = null;
		}
	}

	/**
	 * Render the component into html.
	 */
	render () {
		const {variant} = this.props;

		return <canvas className={`background-canvas background-canvas-${variant}`} aria-hidden="true" ref={this.canvas}/>;
	}

	/**
	 * Whether this surface is the one the current viewport shows. The page field fills the gutters on
	 * anything wide enough to have them; the header field takes over on phones, where the header bar is
	 * the only piece of backdrop big enough to be worth drawing.
	 */
	IsActive () {
		const {variant} = this.props;

		return variant === kBackgroundHeader ? this.mobileQuery.matches : !this.mobileQuery.matches;
	}

	/**
	 * Start drawing, or draw a single frame where motion is unwelcome.
	 */
	Start () {
		if (!this.IsActive ()) {
			return;
		}

		if (this.reducedMotionQuery.matches) {
			this.DrawOnce ();
			return;
		}

		if (this.running) {
			return;
		}

		this.running = true;
		RegisterBackgroundSurface (this);
	}

	/**
	 * Stop drawing.
	 */
	Stop () {
		if (!this.running) {
			return;
		}

		this.running = false;
		UnregisterBackgroundSurface (this);
	}

	/**
	 * Draw a single still frame, for reduced motion and for scheme changes while paused.
	 */
	DrawOnce () {
		if (!this.IsActive ()) {
			return;
		}

		this.Draw (kFrameInterval, false);
	}

	/**
	 * Clear and draw.
	 */
	Draw (deltaSeconds, animated) {
		this.canvasContext.clearRect (0, 0, this.canvas.current.width, this.canvas.current.height);

		//The field itself reacts to the pointer; nothing is painted over it.
		this.renderer.SetPointer (this.pointer, kPointerRadius);

		this.renderer.Draw (this.canvasContext, deltaSeconds, this.colors, animated);
	}

	/**
	 * Take the drawing colors from the theme tokens, so the background follows the color scheme.
	 */
	ReadColors () {
		const rootStyle = window.getComputedStyle (document.documentElement);

		this.colors = {
			accent: rootStyle.getPropertyValue ('--color-accent').trim (),
			text: rootStyle.getPropertyValue ('--color-backdrop-text').trim ()
		};
	}

	/**
	 * Match the canvas to the box it occupies, in device pixels. Read from the element rather than the
	 * window, the header surface being the size of the header rather than the viewport.
	 */
	Size () {
		const width = this.canvas.current.clientWidth;
		const height = this.canvas.current.clientHeight;
		const pixelRatio = Math.min (kMaxPixelRatio, window.devicePixelRatio || 1);

		this.canvas.current.width = Math.floor (width * pixelRatio);
		this.canvas.current.height = Math.floor (height * pixelRatio);

		//Draw in css pixels and let the transform handle the density.
		this.canvasContext.setTransform (pixelRatio, 0, 0, pixelRatio, 0, 0);

		this.renderer.Resize (width, height, pixelRatio);
	}

	/**
	 * Viewport changed. Which surface is the active one can change with it, so this both resizes and
	 * hands over between the page field and the header field.
	 */
	OnResize () {
		if (!this.IsActive ()) {
			this.Stop ();
			return;
		}

		this.Size ();

		if (this.running) {
			return;
		}

		this.DrawOnce ();
		this.Start ();
	}

	/**
	 * Follow the pointer. Ignored under reduced motion, so a still backdrop stays still.
	 */
	OnPointerMove (e) {
		if (this.reducedMotionQuery.matches || this.mobileQuery.matches) {
			return;
		}

		this.pointer = {x: e.clientX, y: e.clientY};
	}

	/**
	 * Pointer left the page, so the field settles back to its own rhythm.
	 */
	OnPointerLeave () {
		this.pointer = null;
	}

	/**
	 * Stop drawing while the tab is in the background.
	 */
	OnVisibilityChange () {
		if (document.hidden) {
			this.Stop ();
			return;
		}

		this.Start ();
	}
}

Background.defaultProps = {
	variant: kBackgroundPage
};

export default Background;
